import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { setShowroomSelectedTab } from 'Apollo/Store/Showroom/UserView/resolvers';
import { ShowroomTab, useGetShowroomSamplesBasketQuery } from 'generated/graphql';

import { useTimeout } from 'lib/Hooks';
import { useGetCurrentShowroomId, useIsShowroomBasketEnabled, useIsShowroomMobileVersion, useIsShowroomUserView } from 'lib/showroom';

import AppBar from '@material-ui/core/AppBar';
import Badge from '@material-ui/core/Badge';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import grey from '@material-ui/core/colors/grey';

import ArrowPopper from 'components/Common/ArrowPopper';
import ShowroomSamplesBasket from 'components/Showroom/SamplesBasket';

import { ReactComponent as SamplesIcon } from 'icons/samples.svg';

import { useStyles } from './styles';

export type ComponentProps = {
	logo: string;
	selectedTab: ShowroomTab;
};

const ShowroomContentHeader = (props: ComponentProps) => {
	const { logo, selectedTab } = props;

	const isUserView = useIsShowroomUserView();
	const showroomId = useGetCurrentShowroomId();
	const displayBasket = useIsShowroomBasketEnabled();

	const classes = useStyles();
	const isMobileVersion = useIsShowroomMobileVersion();

	const { t } = useTranslation();

	const { run } = useTimeout();
	const { data } = useGetShowroomSamplesBasketQuery({ variables: { showroomId }, skip: !displayBasket });

	const headerRef = React.useRef<HTMLElement>();
	const basketRef = React.useRef<any>();
	const lastElementCount = React.useRef(data ? data.showroomSamplesBasket : 0);

	const [showAddedToBasket, setShowAddedToBasket] = React.useState(false);
	const popperAnchor = React.useMemo(() => (!!showAddedToBasket ? basketRef.current : null), [showAddedToBasket]);

	// Update this condition as we add more actions into the header
	const displayActions = displayBasket;

	React.useEffect(() => {
		// Margin correction for scrollbar in preview mode (inside dialog)
		if (!isUserView && !isMobileVersion) {
			const dialogElement = document.getElementById('showroomLayoutDialogContent');
			if (dialogElement && dialogElement.scrollHeight > dialogElement.clientHeight) {
				headerRef.current.classList.add(classes.showroomHeaderPreviewScroll);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserView, isMobileVersion]);

	React.useEffect(() => {
		if (data) {
			if (data.showroomSamplesBasket.length > lastElementCount.current) {
				setShowAddedToBasket(true);
				run(() => setShowAddedToBasket(false), 2000);
			}
			lastElementCount.current = data.showroomSamplesBasket.length;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return (
		<AppBar
			className={clsx(classes.showroomAppBar, { [classes.showroomHeaderPreviewView]: !isUserView })}
			position="fixed"
			elevation={0}
			ref={headerRef}
		>
			<div className={clsx(classes.showroomHeader, { [classes.showroomHeaderMobile]: isMobileVersion })}>
				<div
					className={clsx(classes.headerLeftSection, {
						[classes.headerLeftSectionEmpty]: isMobileVersion || !displayActions
					})}
				></div>
				<div
					className={clsx(classes.showroomLogo, {
						[classes.showroomLogoMobile]: isMobileVersion,
						[classes.showroomLogoMobileLeftAlign]: isMobileVersion && displayActions
					})}
					id="showroomLogoContainer"
					style={{ backgroundImage: `url(${logo})` }}
				></div>
				{displayActions ? (
					<div className={classes.actionsContainer}>
						{displayBasket ? (
							<IconButton
								id="showroomContentHeaderBasketIconButton"
								className={clsx(classes.actionButton, {
									[classes.selectedButton]: selectedTab === ShowroomTab.Samples,
									[classes.actionButtonMobile]: isMobileVersion
								})}
								onClick={() => setShowroomSelectedTab(ShowroomTab.Samples)}
								disableRipple
								disableFocusRipple
							>
								<Badge
									badgeContent={data.showroomSamplesBasket.length}
									color="secondary"
									classes={{
										badge: clsx(classes.basketBadge, { [classes.basketBadgeMobile]: isMobileVersion })
									}}
									variant={isMobileVersion ? 'dot' : 'standard'}
								>
									<SamplesIcon ref={basketRef} />
								</Badge>
							</IconButton>
						) : null}
					</div>
				) : null}
			</div>
			<Collapse in={!!selectedTab}>
				<div className={clsx(classes.tabContainer, { [classes.tabContainerMobile]: isMobileVersion })}>
					{(() => {
						switch (selectedTab) {
							case ShowroomTab.Samples:
								return <ShowroomSamplesBasket />;
							default:
								return null;
						}
					})()}
				</div>
			</Collapse>
			<ArrowPopper
				id="ShowroomContentHeaderPopper"
				className={classes.headerPopper}
				anchor={popperAnchor}
				TransitionComponent={Collapse}
				transitionProps={{
					timeout: {
						enter: 200,
						exit: 300
					}
				}}
				backgroundColor={grey[900]}
				placement="bottom"
				popperProps={{ keepMounted: true, disablePortal: true }}
			>
				<div className={classes.headerPopperContent}>{t('showroom.samples.list.added')}</div>
			</ArrowPopper>
		</AppBar>
	);
};

export default React.memo(ShowroomContentHeader);
